<template>
  <div class="p-4 h-full flex flex-column align-items-center">
    <h1 class="text-2xl font-bold mb-4">Analyseur de Sécurité E-mail</h1>
    <div ref="widgetContainer" class="widget-container"></div>

    <div class="explanation-container mt-6">
      <Accordion :activeIndex="[0]">
        <AccordionTab header="Les 3 Piliers de l'Authentification E-mail">
            <p class="line-height-3 m-0">
              Pour garantir la délivrabilité et la sécurité de vos e-mails, trois standards techniques fonctionnent de concert. Ils permettent aux serveurs de réception de vérifier que les messages qui semblent venir de vous sont bien légitimes.
            </p>
            <ul class="list-disc ml-4 mt-3">
                <li class="mb-2"><strong>SPF (Sender Policy Framework):</strong> Définit une "liste blanche" des adresses IP et des serveurs autorisés à envoyer des e-mails pour votre domaine.</li>
                <li class="mb-2"><strong>DKIM (DomainKeys Identified Mail):</strong> Appose une signature numérique inviolable à vos e-mails, prouvant que le contenu n'a pas été altéré et qu'il provient bien d'un expéditeur autorisé.</li>
                <li class="mb-2"><strong>DMARC (Domain-based Message Authentication...):</strong> Unifie SPF et DKIM. Il instruit les serveurs de réception sur la marche à suivre (ne rien faire, mettre en quarantaine ou rejeter) si un e-mail échoue aux vérifications et active l'envoi de rapports d'analyse détaillés.</li>
            </ul>
        </AccordionTab>
        <AccordionTab header="Protéger vos Domaines Inutilisés (Parking)">
            <p class="line-height-3 m-0">
              Les noms de domaine enregistrés par vos équipes (pour protéger une marque, variantes, etc.) mais non utilisés pour l'envoi d'e-mails sont des vecteurs d'attaque courants pour l'usurpation d'identité (spoofing).
            </p>
            <p class="mt-3 line-height-3 m-0">La solution est de "verrouiller" ces domaines en publiant un enregistrement SPF qui interdit formellement tout envoi :</p>
            <pre class="surface-ground p-3 mt-3 border-round text-color-secondary"><code>v=spf1 -all</code></pre>
            <p class="mt-3 line-height-3 m-0">Associez-le à un enregistrement DMARC tout aussi strict pour ordonner le rejet de tout e-mail frauduleux :</p>
            <pre class="surface-ground p-3 mt-3 border-round text-color-secondary"><code>v=DMARC1; p=reject;</code></pre>
            <p class="mt-3 line-height-3 m-0">L'application de ces deux enregistrements sur vos domaines non utilisés est la méthode la plus simple et la plus efficace pour les neutraliser contre toute tentative d'usurpation.</p>
        </AccordionTab>
        <AccordionTab header="Sécuriser l'ensemble de vos Sous-domaines (Wildcard)">
            <p class="line-height-3 m-0">Pour étendre la protection à l'infinité de sous-domaines qui peuvent être créés ou usurpés (<code>info.votredomaine.com</code>, <code>secure-login.votredomaine.com</code>, etc.), l'enregistrement <strong>SPF Wildcard</strong> est une stratégie de défense en profondeur.</p>
            <p class="mt-3 line-height-3 m-0">En créant un enregistrement DNS de type TXT pour le nom d'hôte spécial <strong><code>*.votredomaine.com</code></strong>, vous définissez une politique de sécurité par défaut pour tous les sous-domaines qui n'ont pas leur propre enregistrement SPF.</p>
            <pre class="surface-ground p-3 mt-3 border-round text-color-secondary"><code>v=spf1 -all</code></pre>
            <p class="mt-3 line-height-3 m-0">Cette règle simple agit comme un filet de sécurité, bloquant les tentatives de spoofing sur des sous-domaines non définis.</p>
            <p class="mt-4 line-height-3 m-0"><strong>Note sur DMARC et les sous-domaines :</strong><br />
              Par défaut, la politique DMARC que vous publiez sur votre domaine principal (ex: <code>votredomaine.com</code>) s'applique automatiquement à tous ses sous-domaines. Cependant, vous pouvez définir une politique différente spécifiquement pour eux en utilisant la balise <strong><code>sp</code></strong> (Subdomain Policy). Par exemple, <code>p=quarantine; sp=reject;</code> mettrait en quarantaine les e-mails échouant sur le domaine principal, mais ordonnerait le rejet de ceux échouant sur les sous-domaines.
            </p>
        </AccordionTab>
      </Accordion>
    </div>

  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useThemeStore } from '@/stores/theme';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';

const themeStore = useThemeStore();
const widgetContainer = ref(null);

const widgetUrl = computed(() => {
  // The external script itself handles the theme by appending /dark_mode or /light_mode
  // We just need to provide the correct URL.
  return themeStore.isDarkMode
    ? 'https://inaubi.powerdmarc.com/analyze/z9673g023vmlx12o/dark_mode'
    : 'https://inaubi.powerdmarc.com/analyze/z9673g023vmlx12o/light_mode';
});

const loadWidget = () => {
  if (widgetContainer.value) {
    // Clear previous widget script to prevent duplicates
    widgetContainer.value.innerHTML = '';

    // Create the script tag that the widget's JS will look for and replace
    const script = document.createElement('script');
    script.setAttribute('data-type', 'domain-analyzer-container');
    script.src = widgetUrl.value;
    script.async = true;

    // Append the script to our container, which will trigger it to load and execute
    widgetContainer.value.appendChild(script);
  }
};

// Load the widget when the component is first mounted
onMounted(() => {
  loadWidget();
});

// Reload the widget whenever the theme changes (and thus the URL changes)
watch(widgetUrl, () => {
  loadWidget();
});
</script>

<style scoped>
.widget-container {
  width: 100%;
  max-width: 800px;
}
.explanation-container {
  width: 100%;
  max-width: 800px;
}
</style>
